import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';

import { ReactComponent as ArrowRight } from '@/assets/icons/arrowright-icon.svg';

const headerMenuHeight = 66;
export const Wrapper = styled(Box)`
  a {
    text-decoration: none;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.almostBlack};
    transition: color 0.2s;
  }
`;

export const SubmenuWrapper = styled.aside<{
  $isOpen: boolean;
  $top?: number;
}>`
  position: fixed;
  inset: 0;
  height: calc(100dvh - ${({ $top }) => $top ?? headerMenuHeight}px);
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  backdrop-filter: ${({ theme }) => theme.components.mobileMenu.backdropFilter};
  z-index: ${({ theme }) => theme.zIndex.mobileMenu};
  overflow: auto;
  transform: ${({ $isOpen }) =>
    $isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.5s;
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
`;

export const List = styled.ul`
  display: flex;
  flex-direction: column;
`;

export const ListItemContent = styled.div`
  text-transform: uppercase;
  border-bottom: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  overflow: visible;
  & > a {
    padding: 20px 16px 15px 20px;
    display: block;
  }
  p {
    font-weight: 600;
  }
`;

export const ListItem = styled.li`
  text-transform: uppercase;
  border-bottom: 1px solid #ebebeb;
  & > a {
    padding: 20px 16px 15px 20px;
    display: block;
  }
  p {
    font-weight: 600;
  }
`;

export const LinkItem = styled(LinkWithLocale)`
  p {
    font-family: ${({ theme }) => theme.fonts.subtitleFont};
  }
`;

export const ParentItem = styled.li`
  text-transform: uppercase;
  border-bottom: 1px solid #ebebeb;
`;

export const SubmenuHeaderWrapper = styled(ParentItem)`
  position: sticky;
  top: 0;
  z-index: 99;
  background: ${({ theme }) => theme.colors.white};
`;

export const InnerList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s065};
  padding-bottom: 20px;
  overflow: visible;
`;

export const InnerListItem = styled.li`
  font-size: ${({ theme }) =>
    theme.components.mobileNav.innerListItem.fontSize};
  overflow: visible;

  & a {
    position: relative;
    z-index: 0;
    font-family: ${({ theme }) => theme.fonts.paragraphFont};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    text-transform: initial;
    padding: 6px 6px;
    margin: 2px -6px;
    border-radius: 6px;
    /*
     * Hover underline drawn with a background gradient that grows from 0 to full
     */
    background-image: linear-gradient(
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.primary}
    );
    background-repeat: no-repeat;
    background-position: left bottom;
    background-size: 0% 2px;
    transition:
      background-size 0.3s,
      color 0.2s;
    &:hover {
      background-size: 100% 2px;
      color: ${({ theme }) => theme.colors.primaryBlue} !important;
    }
    &:focus:not(:focus-visible) {
      outline: none;
    }
    &:focus-visible {
      z-index: 1;
      outline: none;
      box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.primaryBlue};
    }
  }
`;

export const ItemContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 20px 16px 15px 20px;
  background-color: transparent;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.almostBlack};

  svg {
    color: ${({ theme }) => theme.colors.almostBlack};
  }

  & > p {
    font-weight: 600;
  }

  &:hover {
    cursor: pointer;
  }
`;

export const ItemContentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.space.s050};
  width: 100%;
  padding: 20px 16px 15px 20px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.almostBlack};
`;

// Title-type items are group headers on mobile: the label expands the submenu
// instead of navigating. Same layout as ParentNavLink, rendered as a button.
export const TitleLabelButton = styled.button`
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  border: 0;
  background: transparent;
  text-align: left;
  color: inherit;
  font: inherit;
  cursor: pointer;
  touch-action: manipulation;

  p {
    font-weight: 600;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryBlue};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

export const ParentNavLink = styled(LinkWithLocale)`
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;

  p {
    font-weight: 600;
  }
`;

export const ExpandSubmenuButton = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: ${({ theme }) => theme.space.s050};
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.colors.almostBlack};
  cursor: pointer;
  line-height: 0;
  touch-action: manipulation;

  svg {
    color: ${({ theme }) => theme.colors.almostBlack};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryBlue};
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const MainMenuSection = styled.div`
  position: relative;
`;

export const SubmenuHeader = styled(ItemContent)`
  position: relative;
  &:hover {
    cursor: pointer;
  }
  p {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  & > svg {
    transform: rotate(180deg);
  }
`;

export const SubmenuHeaderBackButton = styled(SubmenuHeader).attrs({
  as: 'button',
  type: 'button',
})`
  border: 0;
  background: transparent;
  font: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primaryBlue};
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const Collection = styled.div`
  width: 100%;
  cursor: pointer;
  &:hover {
    img {
      -ms-transform: scale(1.3);
      -webkit-transform: scale(1.3);
      transform: scale(1.3);
    }
    p {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
  img {
    transform: scale(1.2);
    transition: transform 500ms ease-in-out;
  }
  p {
    transition: color 500ms ease-in-out;
  }
`;

export const CollectionLink = styled(LinkWithLocale)`
  width: 100%;
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover img {
    -ms-transform: scale(1.3);
    -webkit-transform: scale(1.3);
    transform: scale(1.3);
  }

  &:hover p {
    color: ${({ theme }) => theme.colors.primary};
  }

  img {
    transform: scale(1.2);
    transition: transform 500ms ease-in-out;
  }

  p {
    transition: color 500ms ease-in-out;
  }
`;

export const CollectionItem = styled.div`
  text-align: center;
  margin-bottom: 22px;
`;

export const CollectionTitle = styled.p`
  white-space: normal;
  font-size: 14px;
  font-weight: 400;
`;

export const CollectionBox = styled.div`
  padding-bottom: 20px;
`;

export const MobileCollectionBox = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  padding: 10px 20px;
  overflow: hidden;
`;

export const LinkBox = styled.div`
  margin-bottom: 40px;
  text-align: center;
  width: 100%;
  & > a {
    text-decoration: none;
    text-transform: uppercase;
    font-size: 14px;
    white-space: nowrap;

    ${({
      theme: {
        colors: { primary, primaryBlue },
        fonts,
      },
    }) => css`
      font-family: ${fonts.paragraphFont};
      color: ${primaryBlue};
      padding-bottom: 5px;
      border-bottom: 2px solid ${primary};
      &:hover {
        color: ${primary};
      }
      transition: color 800ms ease-in-out;
    `}
  }
`;

export const ArrowRightIcon = styled(ArrowRight)``;
